import { ReactNode } from "react";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";
import { fieldStatus } from "../../../utils/contactMerges";
import StatusPill, { StatusPillColour } from "../StatusPill";

export interface IMergeField {
  label: string;
  value: string | null | undefined;
}

export interface IMergeStat {
  label: string;
  count: number;
}

interface MergeColumnProps {
  title: string;
  subtitle?: string;
  isKept: boolean;
  // The same rows, in the same order, in every column, so they line up across the dialog.
  fields: IMergeField[];
  // The kept row's, to mark what this one fills or disagrees with. Ignored on the kept column.
  keptFields: IMergeField[];
  stats: IMergeStat[];
  refusal?: string | null;
  onRemove?: () => void;
  onKeep?: () => void;
  // Below the counts: a list of leads, the people on an account.
  children?: ReactNode;
}

// One row in a merge dialog - a contact or an account - laid out so it reads straight across against
// the others: details in fixed rows, what this row would fill on the kept one in green, what it
// disagrees with in amber (the kept row's wins), then the counts of what moves.
export default function MergeColumn({
  title,
  subtitle,
  isKept,
  fields,
  keptFields,
  stats,
  refusal,
  onRemove,
  onKeep,
  children,
}: MergeColumnProps) {
  const { t } = useTranslation("accounts");

  return (
    <div
      className={classNames(
        "flex min-w-0 flex-col gap-4 rounded-lg border p-4",
        isKept ? "border-green-500 bg-green-50/40 ring-1 ring-green-500" : "border-gray-200 bg-white",
        refusal ? "border-red-300 ring-1 ring-red-300" : ""
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <StatusPill
            colour={isKept ? StatusPillColour.Green : StatusPillColour.Neutral}
            text={isKept ? t("Keeping") : t("Merging in")}
          />
          <p className="mt-2 truncate text-base font-semibold text-gray-900" title={title}>
            {title}
          </p>
          {subtitle && <p className="truncate text-sm text-gray-500">{subtitle}</p>}
        </div>
        {onRemove && (
          <button type="button" onClick={onRemove} aria-label={t("Take out of the merge")} title={t("Take out of the merge")}>
            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
        {fields.map((field, index) => {
          const status = isKept ? "same" : fieldStatus(keptFields[index]?.value, field.value);

          return (
            <div key={field.label} className="contents">
              <dt className="text-gray-500">{field.label}</dt>
              <dd className="flex min-w-0 items-center gap-1.5">
                <span
                  className={classNames(
                    "truncate",
                    !field.value?.trim() ? "text-gray-300" : "text-gray-900",
                    status === "fills" && "font-medium text-green-700",
                    status === "differs" && "font-medium text-amber-700"
                  )}
                  title={field.value ?? ""}
                >
                  {field.value?.trim() || "—"}
                </span>
                {status === "fills" && <span className="shrink-0 text-xs text-green-700">{t("fills blank")}</span>}
                {status === "differs" && <span className="shrink-0 text-xs text-amber-700">{t("not kept")}</span>}
              </dd>
            </div>
          );
        })}
      </dl>

      <div className="grid grid-cols-3 gap-2">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-md bg-gray-50 px-2 py-1.5 text-center">
            <p className={classNames("text-base font-semibold", stat.count > 0 ? "text-gray-900" : "text-gray-300")}>{stat.count}</p>
            <p className="text-xs text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>

      {children}

      {refusal && <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">{refusal}</p>}

      {onKeep && (
        <button
          type="button"
          onClick={onKeep}
          className="mt-auto self-start rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {t("Keep this one instead")}
        </button>
      )}
    </div>
  );
}
